import { Component, EventEmitter, Input, OnChanges, SimpleChanges, Output } from '@angular/core';
import {
    ProcessReportTableDomainService
} from "app_sasha/pages/process-report/components/process-report-table/process-report-table-domain.service";
import { IConfigurableTable, IConfigurableTableColumn } from "libraries/interfaces/i-configurable-table";
import { IStatusColorationRule } from "libraries/component/status-tag/status-tag.component";
import { ILoader } from "libraries/component/loader/ILoader";
import { INotification } from "libraries/interfaces/INotification";
import { IPaginationRecordset } from "libraries/component/pagination/IPagination";
import { Wording } from "./process-report-table-wording";
import { IProcessReportSearchParams } from "app_sasha/pages/process-report/process-report-page.component";
import { IProcessReportBookingItem } from "libraries/interfaces/process-report/i-process-report-response";



export enum ENavigationDirection {
    prev = "prev",
    next = "next",
}

export interface INavigationOutput {
    direction?: ENavigationDirection;
    id?: number;
    count?: number;
}

@Component({
    selector: 'process-report-table',
    template: `
        <card *ngIf="bookingArray?.length || notification?.active || loader?.active">
            <div class="card-header-content">
                <div class="header-left-part">
                    <h1>{{wording.title }}</h1>
                </div>
            </div>
            <div class="card-body-content" *ngIf="!bookingArray?.length && loader?.active">
                <loader [type]="loader.type" [message]="loader.message"></loader>
            </div>
            <div class="card-body-content" *ngIf="!bookingArray?.length && notification?.active">
                <notification [type]="notification.type">{{notification.message}}</notification>
            </div>
            <div class="card-body-content" *ngIf="bookingArray?.length && tableConfig">
                <table mat-table [dataSource]="bookingArray" #myTable>

                    <ng-container *ngFor="let colKey of displayedColumns">
                        <ng-container [matColumnDef]="colKey">
                            <th-text [isLeft]="true"
                                     *matHeaderCellDef>{{(tableConfig | findColInConfig:colKey).defaultLabel }}</th-text>
                            <ng-container *matCellDef="let element; let rowIndex = index; let odd = odd">
                                <td-text [isOdd]="odd" [isLightColor]="true" [isTop]="true">
                                    <div *ngFor="let fieldKey of (tableConfig | findColInConfig:colKey).fieldKeyArray">

                                        <ng-container *ngIf="(element | computeValue:fieldKey)?.value">
                                            <ng-container
                                                    *ngIf="(element | computeValue:fieldKey).type === 'xStatus'">
                                                <booking-status mode="icon"
                                                                [status]="(element | computeValue:fieldKey).value"></booking-status>
                                                <booking-status mode="label"
                                                                [status]="(element | computeValue:fieldKey).value"
                                                                [label]="null"></booking-status>
                                            </ng-container>
                                            <ng-container
                                                    *ngIf="(element | computeValue:fieldKey).type === 'processStatus'">
                                                <booking-status mode="icon"
                                                                [status]="(element | computeValue:fieldKey).value"
                                                                [statusColorationRule]="statusColorationRule"></booking-status>
                                                <booking-status mode="label"
                                                                [status]="(element | computeValue:fieldKey).value"
                                                                [label]="null"
                                                                [statusColorationRule]="statusColorationRule"></booking-status>
                                            </ng-container>
                                            <ng-container
                                                    *ngIf="(element | computeValue:fieldKey).type === 'string'">
                                                {{(element | computeValue:fieldKey).value}}
                                            </ng-container>
                                            <ng-container
                                                    *ngIf="(element | computeValue:fieldKey).type === 'hostCode'">
                                                <ng-container *ngIf="element.syscode; else noSyscodeForLink">
                                                    <a [routerLink]="element | computeProcessBookingLink"
                                                       class="booking-link">
                                                        {{(element | computeValue:fieldKey).value}}
                                                    </a>
                                                </ng-container>
                                                <ng-template #noSyscodeForLink>
                                                    {{(element | computeValue:fieldKey).value}}
                                                </ng-template>

                                            </ng-container>
                                        </ng-container>

                                    </div>
                                </td-text>
                            </ng-container>
                        </ng-container>
                    </ng-container>

                    <ng-container *ngIf="tableConfig.isVisibleHeader">
                        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                    </ng-container>
                    <ng-container *matRowDef="let row; columns: displayedColumns;let odd=odd; let rowIndex=index">
                        <tr mat-row></tr>
                    </ng-container>
                </table>
                <div class="pagination-container">
                    <process-report-pagination [responseRecordSet]="responseRecordSet"
                                               [params]="params"
                                               (onNavigate)="onNavigate.emit($event)"></process-report-pagination>
                </div>
            </div>
        </card>
    `,
    styles: [
        `
            table {
                margin-top: 0;
            }

            a.booking-link {
                color: #337ab7
            }

            tr.mat-row {
                height: unset;
            }

            booking-status {
                display: inline-block;
                margin: .325rem .2rem .325rem 0;
            }
            
            .pagination-container {
                display: flex;
                justify-content: center;
                background: #f4f4f4;
            }
        `
    ]
})
export class ProcessReportTableComponent implements OnChanges {

    @Input()
    public bookingArray: IProcessReportBookingItem[];

    @Input()
    public loader: ILoader;

    @Input()
    public responseRecordSet: IPaginationRecordset;

    @Input()
    public notification: INotification;

    @Input()
    public params: IProcessReportSearchParams;

    @Output()
    public readonly onNavigate: EventEmitter<INavigationOutput> = new EventEmitter<INavigationOutput>();

    public statusColorationRule: IStatusColorationRule = {
        green: "OK",
        red: "Danger",
    };

    public displayedColumns: string[] = [];
    public tableConfig: IConfigurableTable;
    public wording: Wording = new Wording();

    constructor(
        private readonly processReportTableDomainService: ProcessReportTableDomainService
    ) {
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (!this.displayedColumns?.length) {
            this.computeCols();
        }
    }

    private computeCols(): void {
        this.processReportTableDomainService.getConfig()
            .subscribe((configuration: IConfigurableTable): void => {
                this.tableConfig = configuration;
                this.displayedColumns = configuration.columnArray
                    .filter((col: IConfigurableTableColumn): boolean => col.isDisplayed)
                    .map((col: IConfigurableTableColumn): string => col.key);
            });
    }
}
